/*
 *  -----------------------------------------------------------------------
 *    <copyright file="custom-report.config.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { useTranslation } from 'hooks';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import AuditEnumValues from 'views/pages/admin/audit-log/definitions/enums';
import {
    getProcessedFieldChangeItems,
    getFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';

interface ReportDefinitionColumnAuditRecord {
    dataField: number;
}
interface ReportDefinitionTabAuditRecord {
    index: number;
    tabType: number;
    columns: ReportDefinitionColumnAuditRecord[];
}

const propArray = ['UserId'];

const ReportDefinitionChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    const { t } = useTranslation();

    const getTabs = (record: any) => {
        const tabs = record.Tabs?.filter((tab: any) => tab);

        if (!tabs?.length) return null;

        return tabs.map((tab: any, index: number) => {
            const columns = tab?.Columns?.length
                ? tab.Columns.map(
                      (column: any) => ({ dataField: column.DataField }) as ReportDefinitionColumnAuditRecord,
                  )
                : ([] as ReportDefinitionColumnAuditRecord[]);

            return {
                tabType: tab.TabType,
                index,
                columns,
            } as ReportDefinitionTabAuditRecord;
        });
    };

    const refactoredOldRecord = {
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, propArray),
        Tabs: getTabs(oldRecord),
    };

    const refactoredNewRecord = {
        ...newRecord,
        ...removePropertiesFromObject(newRecord, propArray),
        Tabs: getTabs(newRecord),
    };

    const arrayOfObjects = {
        Tabs: {
            valueResolver: (recordData: ReportDefinitionTabAuditRecord, item: any) => {
                let value = '';
                value += 'Data Set: ';
                value += t(`reports:${AuditEnumValues.ReportDefinitionTabs[recordData.tabType]}`);
                value += `, Order: ${recordData.index}. `;
                recordData.columns.forEach((column: ReportDefinitionColumnAuditRecord, index: number) => {
                    value += 'Column: ';
                    value += t(`reports:${AuditEnumValues.ReportDefinitionFieldTypes[column.dataField]}`);
                    value += `, Order: ${index}${index === recordData.columns.length - 1 ? '. ' : '; '}`;
                });
                return value;
            },
        },
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjects,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { ReportDefinitionChangeGrid };
